import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShoppingCart, Copy, MessageCircle, Download, Pill, UtensilsCrossed } from "lucide-react";
import { copyShoppingList, openWhatsAppWithText, downloadShoppingListText } from "@/lib/nutritionExport";

export default function ShoppingList() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState("7");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/nutrition/shopping-list", { params: { days } });
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]);

  const handleCopy = async () => {
    if (!data?.export_text) return;
    await copyShoppingList(data.export_text);
    toast.success("Liste copiée — collez dans Notes ou Messages");
  };

  const handleWhatsApp = () => {
    if (!data?.export_text) return;
    openWhatsAppWithText(data.export_text);
  };

  const handleDownload = () => {
    if (!data?.export_text) return;
    downloadShoppingListText(data.export_text);
    toast.success("Fichier téléchargé");
  };

  return (
    <div data-testid="shopping-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Liste de courses</h1>
            <p className="text-slate-400 mt-1 text-sm">Aliments du plan diet + compléments prescrits</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 jours</SelectItem>
              <SelectItem value="7">7 jours</SelectItem>
              <SelectItem value="14">14 jours</SelectItem>
            </SelectContent>
          </Select>
          {data?.export_text && (
            <>
              <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
                <Copy className="w-4 h-4" /> Copier
              </Button>
              <Button variant="outline" size="sm" onClick={handleWhatsApp} className="gap-1.5 text-green-700 border-green-200">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5">
                <Download className="w-4 h-4" /> Télécharger
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="mt-6 space-y-4">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
      ) : !data?.sections?.length ? (
        <div className="text-center py-16 mt-8 text-slate-400">
          <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Aucun élément — votre coach doit vous assigner un plan alimentaire ou compléments</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-slate-500">
            {data.total_items} article{data.total_items > 1 ? "s" : ""} pour {data.days} jour{data.days > 1 ? "s" : ""}
          </p>
          {data.sections.map((section) => (
            <Card key={section.title} className="border-slate-100">
              <CardContent className="p-5">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
                  {section.title === "Compléments" ? (
                    <Pill className="w-4 h-4 text-indigo-500" />
                  ) : (
                    <UtensilsCrossed className="w-4 h-4 text-emerald-500" />
                  )}
                  {section.title}
                  <Badge variant="secondary" className="text-[10px]">{section.items.length}</Badge>
                </h2>
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 text-sm border-b border-slate-50 pb-2 last:border-0">
                      <div>
                        <span className="font-medium text-slate-800">{item.label}</span>
                        {item.brand && <span className="text-indigo-600 ml-1">— {item.brand}</span>}
                        {item.quantity_hint && (
                          <span className="text-slate-400 ml-2 text-xs">{item.quantity_hint}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 shrink-0">
                        {item.dosage && <span>{item.dosage}</span>}
                        {item.timing && <span className="ml-1">· {item.timing}</span>}
                        {item.source && <p className="mt-0.5">{item.source}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}